import { Link, useLocation } from "react-router-dom";
import MarketplaceShell from "./MarketplaceShell";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/BrandContext";
import { buildHostUrl } from "../../config/hostRouting";
import { isSalonBusinessCpaAccountant } from "../../utils/accountingRoles";
import { roleLabel } from "../../utils/salonRoles";

function buildOpsRegisterUrl(brand, params) {
  return buildHostUrl(brand?.opsHost || "ellyanna.com", "/register", `?${params}`);
}

export default function MarketplaceAccessNeededPage() {
  const location = useLocation();
  const brand = useBrand();
  const { user, role } = useAuth();
  const params = new URLSearchParams(location.search || "");
  const roleFromQuery = params.get("role");
  const currentRole = role || user?.role || roleFromQuery || "";
  const isAccountingAccountant = isSalonBusinessCpaAccountant(currentRole);
  const currentRoleLabel = currentRole ? roleLabel(currentRole) : "Staff";
  const independentParams = new URLSearchParams({
    role: "independent_professional",
    source: "myboothshare_access_needed",
    intent: "independent_professional",
  });
  const salonOwnerParams = new URLSearchParams({
    role: "owner",
    source: "myboothshare_access_needed",
    intent: "salon_owner",
  });

  return (
    <MarketplaceShell>
      <section className="space-y-6">
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-[#2f3566] px-6 py-7 text-white">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-200">
              MyBoothShare Access
            </p>
            <h1 className="mt-3 max-w-4xl text-3xl font-semibold md:text-4xl">
              {isAccountingAccountant
                ? "This account is for SalonBusinessCPA review work"
                : "MyBoothShare is for workspace renters and salon operators"}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200 md:text-base">
              {isAccountingAccountant
                ? "Your accountant login is limited to SalonBusinessCPA. Use it to review accounting records, document requests, and monthly accounting work."
                : "Your current Ellyanna Booking role is set up for staff scheduling inside your salon. To use MyBoothShare, you can join as an independent professional renting workspace, or register your own salon to list and manage workspace rentals."}
            </p>
          </div>

          <div className="grid gap-6 px-6 py-6 lg:grid-cols-[0.9fr,1.1fr]">
            <aside className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Current access
              </div>
              <div className="mt-3 text-2xl font-semibold text-slate-900">
                {currentRoleLabel}
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {isAccountingAccountant
                  ? "This role is not an Ellyanna Booking operator role and is not a MyBoothShare renter or operator role."
                  : "This role can keep using Ellyanna Booking for the salon staff workflow. MyBoothShare account tools need an owner, manager, receptionist, independent professional, or super admin role."}
              </p>
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm leading-6 text-amber-900">
                {isAccountingAccountant
                  ? "If you also need Ellyanna Booking or MyBoothShare access, that should be granted through a separate salon user role."
                  : "If you should have access for your current salon, ask the salon owner or manager to update your Ellyanna Booking role."}
              </div>
            </aside>

            {isAccountingAccountant ? (
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  SalonBusinessCPA
                </div>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  Continue to accounting review
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Open your SalonBusinessCPA workboard to review assigned salons,
                  records, requests, and follow-up items.
                </p>
                <a
                  href={buildHostUrl(
                    brand?.accountingHostName || "salonbusinesscpa.com",
                    "/workboard"
                  )}
                  className="mt-5 inline-flex rounded-full bg-[#2f3566] px-5 py-3 text-sm font-semibold text-white hover:bg-[#252a53]"
                >
                  Open SalonBusinessCPA
                </a>
              </div>
            ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Option 1
                </div>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  Register as an independent professional
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Use this if you rent a chair, room, station, or workspace and want your
                  own paid Ellyanna Booking account connected to MyBoothShare.
                </p>
                <a
                  href={buildOpsRegisterUrl(brand, independentParams.toString())}
                  className="mt-5 inline-flex rounded-full bg-rose-500 px-5 py-3 text-sm font-semibold text-white hover:bg-rose-600"
                >
                  Register as an Independent Professional
                </a>
              </div>

              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Option 2
                </div>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  Register a salon
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  Use this if you own or manage a salon and want to list workspace
                  rentals, manage renters, and run your salon through Ellyanna Booking.
                </p>
                <a
                  href={buildOpsRegisterUrl(brand, salonOwnerParams.toString())}
                  className="mt-5 inline-flex rounded-full bg-[#2f3566] px-5 py-3 text-sm font-semibold text-white hover:bg-[#252a53]"
                >
                  Register a Salon
                </a>
              </div>
            </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 border-t border-slate-200 px-6 py-5">
            {isAccountingAccountant ? (
              <a
                href={buildHostUrl(
                  brand?.accountingHostName || "salonbusinesscpa.com",
                  "/workboard"
                )}
                className="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Back to SalonBusinessCPA
              </a>
            ) : (
              <>
                <a
                  href={brand.opsHomeUrl}
                  className="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Back to Ellyanna Booking
                </a>
                <Link
                  to="/search"
                  className="rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Search Workspaces
                </Link>
              </>
            )}
          </div>
        </article>
      </section>
    </MarketplaceShell>
  );
}
